import { getProvider } from "@aux0/connectors";
import { ArrowLeft01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { AppLink } from "@/components/common/app-link";
import { ProviderMark, Tag } from "@/components/common/badges";
import { CopyBlock } from "@/components/common/copy-button";
import { Icon } from "@/components/common/icon";
import { Field, Page, PageTitle, Panel } from "@/components/common/page";
import { PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { q } from "@/data/queries";
import { formatDateTime } from "@/lib/format";
import { projectPath } from "@/lib/nav";

export const Route = createFileRoute("/p/$project/contacts/$contactId")({
  loader: async ({ context, params }) => {
    const contact = await context.queryClient.ensureQueryData(
      q.contact(params.contactId),
    );
    if (!contact) throw notFound();
    await Promise.all([
      context.queryClient.ensureQueryData(q.contactGroups()),
      context.queryClient.ensureQueryData(q.account(contact.accountId)),
    ]);
    return { contact };
  },
  pendingComponent: PageSkeleton,
  component: ContactPage,
});

function ContactPage() {
  const { project } = Route.useParams();
  const { contact } = Route.useLoaderData();
  const { data: groups } = useSuspenseQuery(q.contactGroups());
  const { data: account } = useSuspenseQuery(q.account(contact.accountId));
  const provider = getProvider(contact.provider);
  const memberships = groups.filter((group) => contact.groupIds.includes(group.id));

  return (
    <Page>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 w-fit text-muted-foreground"
        asChild
      >
        <AppLink to={projectPath(project, "/contacts")}>
          <Icon icon={ArrowLeft01Icon} className="size-3.5" />
          Contacts
        </AppLink>
      </Button>

      <PageTitle
        leading={<ProviderMark provider={contact.provider} size="lg" />}
        title={contact.name}
        description={contact.organizations
          .map((org) => [org.title, org.name].filter(Boolean).join(" at "))
          .join(" · ")}
        meta={
          <>
            {contact.starred ? <Tag mono>starred</Tag> : null}
            {contact.inferred ? (
              <Tag mono className="border-wait/30 bg-wait-soft/60 text-wait">
                derived from mail
              </Tag>
            ) : null}
            <span className="mono-id">{contact.id}</span>
          </>
        }
      />

      {contact.inferred ? (
        <div className="rounded-lg border border-wait/30 bg-wait-soft/40 px-4 py-3.5">
          <h2 className="text-[13.5px] font-medium text-wait">
            Aux0 has never seen this person in an address book
          </h2>
          <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.55] text-ink-soft">
            The address came out of message headers. It is in autocomplete because that
            is what makes autocomplete useful, and it is not written back to the
            provider — a "sync my contacts" feature that pushed it would be adding
            people to someone's address book without being asked.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel
            title={`Email (${contact.emails.length})`}
            description="Every address the provider holds, not just the first"
            bodyClassName="divide-y"
          >
            {contact.emails.map((email) => (
              <div
                key={email.address}
                className="flex flex-wrap items-center gap-3 px-4 py-2.5"
              >
                <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
                  {email.address}
                </span>
                {email.label ? <Tag mono>{email.label}</Tag> : null}
                {email.primary ? (
                  <Tag className="border-brand/30 bg-brand-soft/60 text-brand">
                    Primary
                  </Tag>
                ) : null}
              </div>
            ))}
          </Panel>

          {contact.phones.length > 0 ? (
            <Panel title={`Phone (${contact.phones.length})`} bodyClassName="divide-y">
              {contact.phones.map((phone) => (
                <div
                  key={phone.number}
                  className="flex flex-wrap items-center gap-3 px-4 py-2.5"
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
                    {phone.number}
                  </span>
                  {phone.label ? <Tag mono>{phone.label}</Tag> : null}
                  {phone.primary ? (
                    <Tag className="border-brand/30 bg-brand-soft/60 text-brand">
                      Primary
                    </Tag>
                  ) : null}
                </div>
              ))}
            </Panel>
          ) : null}

          {contact.organizations.length > 0 ? (
            <Panel title="Organisations" bodyClassName="px-4 py-2">
              <dl>
                {contact.organizations.map((org) => (
                  <Field key={org.name} label={org.name}>
                    {[org.title, org.department].filter(Boolean).join(" · ") || "—"}
                  </Field>
                ))}
              </dl>
            </Panel>
          ) : null}

          {contact.note ? (
            <Panel
              title="Note"
              description="Free text on the provider side, which is where a CRM's own data usually ends up"
              bodyClassName="p-4"
            >
              <p className="max-w-[76ch] text-[13px] leading-[1.6] whitespace-pre-wrap">
                {contact.note}
              </p>
            </Panel>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="Where it came from" bodyClassName="px-4 py-2">
            <dl>
              <Field label="Provider">{provider?.name ?? contact.provider}</Field>
              <Field label="Account">
                <AppLink
                  to={projectPath(project, `/accounts/${contact.accountId}`)}
                  className="text-brand hover:underline"
                >
                  {account?.handle ?? contact.accountId}
                </AppLink>
              </Field>
              <Field label="Provider id">
                <span className="font-mono text-[12px] break-all">
                  {contact.providerId}
                </span>
              </Field>
              <Field label="Created">{formatDateTime(contact.createdAt)}</Field>
              <Field label="Updated">{formatDateTime(contact.updatedAt)}</Field>
            </dl>
          </Panel>

          {memberships.length > 0 ? (
            <Panel title="Groups" bodyClassName="flex flex-wrap gap-1.5 p-4">
              {memberships.map((group) => (
                <Tag key={group.id} mono={group.system}>
                  {group.name}
                </Tag>
              ))}
            </Panel>
          ) : null}

          <Panel title="Fetch this contact" bodyClassName="p-4">
            <CopyBlock
              value={`curl https://api.aux0.com/v1/contacts/${contact.id} \\\n  -H "Authorization: Bearer $AUX0_API_KEY"`}
            />
          </Panel>
        </div>
      </div>
    </Page>
  );
}
